import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { Transition } from '@headlessui/react';
import { useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import Search from '@/components/search';
import Faq from '@/components/content/faq';
import ContentHeadline from '@/components/content/headline';
import PartnerStats from '@/components/partner/stats';
import Breadcrumbs from '@/components/globals/breadcrumbs';
import CruiseSlider from '@/components/content/cruise-slider';
import SEO from '@/components/helper/seo';
import BoatMasonry from '@/components/content/boat-masonry';
import Hero from '@/components/content/hero';
import { fetchAPI as connectApi } from '@/lib/connect';
import PartnerFeatures from '@/components/partner/features';
import parse from '@/components/helper/image';
import LegalIcon from '../../../public/assets/icons/legal.svg';

const PartnerAreaMap = dynamic(() => import('@/components/partner/map'), { ssr: false });

export const getServerSideProps = async ({ params, query, locale }) => {
  const [res] = await Promise.all([
    await connectApi(`/partner/${params.slug}`, { uri: params.slug }),
  ]);

  if (res?.data === undefined || res.data === null) {
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
    };
  }

  const filter = {
    team_id: res.data.id,
    group: 'model',
    typ: 'w',
    duration: 7,
    ...query,
  };

  const search = await connectApi('/search', filter);

  return {
    props: {
      team: res.data,
      search,
      filter,
      ...await serverSideTranslations(locale, ['boat', 'common', 'footer']),
    },
    // revalidate: 10, // In seconds
  };
};

export default function PartnerPage({ team, search, filter }) {
  const [checked, setChecked] = useState(true);
  const router = useRouter();
  const photoUrl = team.basements.length > 0 ? { publicUrl: team.basements[0].photo_url ? `${team.basements[0].photo_url}?w=2000` : null } : null;

  console.log(photoUrl);

  const faq = {
    content: {
      items: team.faq.map((i) => ({
        header: i.question,
        bodytext: i.answer,
      })),
    },
  };

  return (
    <div>
      <SEO
        title={`Hausboot mieten bei ${team.name} | Bootsreisen Partner`}
        description={`Mieten Sie ein Hausboot bei unserem Partner ${team.name}. Wählen Sie Ihr Traumboot unter ${team.counts.boats} Booten in ${team.countries.length} Ländern.`}
      />
      <Hero
        title={(<div>Hausboot mieten bei</div>)}
        pretitle={`Bootsurlaub mit ${team.name} in ${team.city}`}
        teaser={team.teaser}
        after={team.name}
        checked={checked}
        photoUrl={photoUrl}
        regionalStatus={team.regional_status}
        setChecked={setChecked}
        switchLabel="Partnerinformationen"
        switchSearchLabel={`${team.name}&apos;s Boote`}
      />

      <Transition
        show={!checked}
        enter="transition-all ease-out duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100 "
        leave="transition-all ease-in duration-150"
        leaveFrom="opacity-100 "
        leaveTo="opacity-0"
      >
        <div className="-mt-24 md:-mt-30">
          <Search data={search.data} meta={search.meta} filter={filter} stayOnPage basements={team.basements} />
        </div>
      </Transition>

      <div
        className="max-w-7xl mx-auto w-full px-6 py-6 lg:py-4 xl:px-0"
      >
        <Breadcrumbs items={[
          {
            label: 'Partner',
            title: 'Partner',
            link: '/partner',
          },
          {
            label: team.name,
            title: team.name,
            link: `/${router.asPath.slice(1)}`,
          },
        ]}
        />

        <PartnerStats partner={team} />
        <div className="grid md:grid-cols-2">
          <div className="p-4 bg-gray-50 relative">
            <ContentHeadline tag={2}>
              Über unseren Partner
              <div className="absolute right-5 top-2">
                <Image
                  src={parse(team.photo_url)}
                  height={40}
                  alt={team.name}
                  title={team.name}
                  width={120}
                  objectFit="contain"
                />
              </div>
            </ContentHeadline>
            <PartnerFeatures partner={team} />
            <div dangerouslySetInnerHTML={{ __html: team.description }} className="prose min-h-[170px]" />
            <div className="flex flex-wrap mt-5">
              <div className="flex items-center">
                <LegalIcon className="h-4 mr-3 flex-shrink-0 fill-primary" />
                {team.agb_url && (
                  <a
                    href={`https://connect.5-anker.com${team.agb_url}`}
                    target="_blank"
                    className="text-sm uppercase text-gray-500"
                    rel="noreferrer"
                  >
                    Allgemeine Geschäftsbedingungen
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 h-[300px] md:h-auto">
            <PartnerAreaMap team={team} />
          </div>
        </div>

        <div className="my-10">
          <ContentHeadline after="Angebote">
            {team.name}
            &apos;s aktuelle
          </ContentHeadline>

          <CruiseSlider searchQueryParams={{
            team_id: team.id, sort: 'sort', group: 'model', limit: 9, duration: 7,
          }}
          />
        </div>
      </div>

      <div className="bg-gray-50">
        <div
          className="max-w-7xl mx-auto w-full px-6 py-6 lg:py-10 xl:px-0 grid gap-5"
        >
          <ContentHeadline after="Boote">
            Beliebte
          </ContentHeadline>
          <BoatMasonry items={team.featured.boats} />
        </div>
      </div>
      <div
        className="max-w-7xl mx-auto w-full px-6 py-6 lg:py-4 xl:px-0"
      >
        <div className="mt-10">
          <Faq item={faq} />
        </div>
      </div>
    </div>
  );
}
